import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { StatusBadge } from '@martinus/styles/client/StatusBadge.js';
import { html, nothing } from 'lit';
import { getSupplementStatusForDate } from '../../api/getSupplementStatus.js';
import type { ContractSupplement, ContractSupplementInnerSection } from '../../entities/ContractSupplement.js';
import { SUPPLEMENT_SECTION_LABEL, SUPPLEMENT_STATUS_PRESENTATION } from './status/supplementStatusPresentation.js';

export type SupplementSelectionCardSupplement = NarrowedEntity<
  ContractSupplement,
  {
    nachtragsNummer: 1;
    subNachtragsnummer: 1;
    startDateAG1: 1;
    startDateAG2: 1;
    endDateAG1: 1;
    endDateAG2: 1;
    startDateGeneral: 1;
    endDateGeneral: 1;
    automaticallyAssignedEndDateOfContract: 1;
    changes: { path: 1 };
    signedAt: 1;
  }
>;

const SectionTimeframe = (props: { label: string; start: Maybe<Date>; end: Maybe<Date>; hasChanges: boolean }) => html`
  <span class="font-semibold">${props.label}</span>
  <span>
    ${
      props.start && props.hasChanges
        ? `${formatCalendarDay(props.start, 'dd.MM.yyyy')} - ${props.end ? formatCalendarDay(props.end, 'dd.MM.yyyy') : 'Offenes Ende'}`
        : 'Keine Änderungen'
    }
  </span>
`;

/**
 * A chip per inner section the supplement changes. Filled when that section is in effect at the selected date,
 * outlined when it is not, which is what makes partial activity visible at a glance.
 */
const SectionChip = (props: { section: ContractSupplementInnerSection; isActive: boolean }) => html`
  <span
    class="rounded px-1.5 py-0.5 text-sm font-semibold ${
      props.isActive ? 'bg-success/20 text-success' : 'border border-base-300 text-base-content/70'
    }"
  >
    ${SUPPLEMENT_SECTION_LABEL[props.section]}
  </span>
`;

/**
 * The content of a supplement row in the timeline dropdown: number, timeframe and status.
 *
 * Deliberately only content, no background, padding or hover of its own: the dropdown already provides the row
 * chrome including its hover and selection colors, and a second background on top of it only fights with them.
 * A tooltip reveals the timeframes of the three inner sections.
 */
export const SupplementSelectionCard = (props: {
  supplement: Maybe<SupplementSelectionCardSupplement>;
  date: Maybe<Date>;
  overrideInactive?: boolean;
  overrideActive?: boolean;
}) => {
  if (!props.date || !props.supplement) return nothing;
  const supplement = props.supplement;

  const { status, activeSections, changedSections, start, end } = getSupplementStatusForDate(supplement, props.date, {
    forcedActive: props.overrideActive,
    forcedInactive: props.overrideInactive,
  });
  const presentation = SUPPLEMENT_STATUS_PRESENTATION[status];

  return html`
    <div class="tooltip flex flex-row items-center gap-3" ${hoverTooltip()}>
      <div class="tooltip-content">
        <div class="flex flex-col gap-2 text-left">
          <span>${presentation.description}</span>
          <div class="grid grid-cols-[auto_1fr] gap-2">
            ${SectionTimeframe({
              label: 'AGAllgemein',
              start: supplement.startDateGeneral,
              end: supplement.endDateGeneral,
              hasChanges: changedSections.includes('general'),
            })}
            ${SectionTimeframe({
              label: 'AG1',
              start: supplement.startDateAG1,
              end: supplement.endDateAG1,
              hasChanges: changedSections.includes('ag1'),
            })}
            ${SectionTimeframe({
              label: 'AG2',
              start: supplement.startDateAG2,
              end: supplement.endDateAG2,
              hasChanges: changedSections.includes('ag2'),
            })}
          </div>
        </div>
      </div>

      <span class="w-10 shrink-0 font-semibold">
        ${supplement.nachtragsNummer}${supplement.subNachtragsnummer ? `.${supplement.subNachtragsnummer}` : ''}
      </span>

      <span class="min-w-0 flex-1 truncate text-sm">
        ${start ? formatCalendarDay(start, 'dd.MM.yyyy') : '---'} –
        ${end ? formatCalendarDay(end, 'dd.MM.yyyy') : 'Offenes Ende'}
      </span>

      ${
        changedSections.length
          ? html`
              <span class="flex shrink-0 flex-row items-center gap-1">
                ${changedSections.map(section => SectionChip({ section, isActive: activeSections.includes(section) }))}
              </span>
            `
          : nothing
      }
      ${StatusBadge({
        tone: presentation.tone,
        label: presentation.label,
        iconClass: presentation.iconClass,
      })}
    </div>
  `;
};
